/**
 * Data layer for `scheduled-email-report-daily-weekly-monthly`. Thin typed fetch wrappers over /api/v1/scheduled-email-report-daily-weekly-monthly.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ScheduledEmailReportDailyWeeklyMonthlyRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListScheduledEmailReportDailyWeeklyMonthlyResponse {
	count: number;
	items: ScheduledEmailReportDailyWeeklyMonthlyRow[];
}

export interface CreateScheduledEmailReportDailyWeeklyMonthlyBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/scheduled-email-report-daily-weekly-monthly";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListScheduledEmailReportDailyWeeklyMonthlyResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListScheduledEmailReportDailyWeeklyMonthlyResponse>(res);
}

export async function create(body: CreateScheduledEmailReportDailyWeeklyMonthlyBody): Promise<ScheduledEmailReportDailyWeeklyMonthlyRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ScheduledEmailReportDailyWeeklyMonthlyRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const scheduledEmailReportDailyWeeklyMonthlyQueryKeys = {
	all: ["scheduled-email-report-daily-weekly-monthly"] as const,
	list: () => [...scheduledEmailReportDailyWeeklyMonthlyQueryKeys.all] as const,
	detail: (id: string) => [...scheduledEmailReportDailyWeeklyMonthlyQueryKeys.all, id] as const,
};
